import { useState } from 'react';
import { useFeature } from '../../lib/features';
import { Link } from 'react-router-dom';
import { ChevronRight, LogOut } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { AppCard } from '../../components/ui/AppCard';
import { AvatarView } from '../../components/ui/AvatarView';
import { Button } from '../../components/ui/Button';
import { Sheet } from '../../components/ui/Sheet';
import { ACCOUNT_TIER_LABEL } from '../../types';

function Row({
  to,
  title,
  onClick,
  className,
}: {
  to?: string;
  title: string;
  onClick?: () => void;
  className?: string;
}) {
  const content = (
    <AppCard className="flex items-center gap-3">
      <span className="flex-1 text-[15px] font-medium">{title}</span>
      <ChevronRight size={18} className="text-black/30 dark:text-white/30" />
    </AppCard>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`text-left ${className ?? ''}`}>
        {content}
      </button>
    );
  }
  return (
    <Link to={to!} className={className}>
      {content}
    </Link>
  );
}

export function SettingsScreen() {
  const aiOn = useFeature('reports.ai');
  const pushOn = useFeature('notifications.push');
  const { profile, logout, channels, isChannelOwner } = useStore();
  const owned = channels.filter(isChannelOwner).length;
  const [showViewer, setShowViewer] = useState(false);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold tracking-tight m-0">Settings</h1>

      {/* The card is the way into Profile edit now - no separate "Profile"
          row below it. The avatar itself still opens the quick preview,
          same as before - its onClick stops the click from also bubbling
          into the card's own <a> and navigating. */}
      <Link to="/settings/profile">
        <AppCard className="flex items-center gap-3.5">
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowViewer(true);
            }}
            className="cursor-pointer"
          >
            <AvatarView symbol={profile.avatarSymbol} colorHex="#3B82F6" imageKey={profile.avatarImageKey} size={72} />
          </div>
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <span className="font-bold text-lg truncate">{profile.name}</span>
            <span className="text-black/50 dark:text-white/50 truncate">{profile.nickname}</span>
            <span className="text-xs text-black/40 dark:text-white/40">
              {owned} / {channels.length} channels
            </span>
          </div>
          <ChevronRight size={18} className="text-black/30 dark:text-white/30 shrink-0" />
        </AppCard>
      </Link>

      <Row to="/settings/plan" title={`Plan — ${ACCOUNT_TIER_LABEL[profile.tier] ?? profile.tier}`} />
      {aiOn && <Row to="/settings/ai" title="AI keys" />}
      <Row to="/settings/appearance" title="Appearance" />
      <Row to="/settings/connection" title="Connection" />
      {pushOn && <Row to="/settings/notifications" title="Notifications" />}
      <Row to="/settings/security" title="Security" />
      <Row to="/settings/stats" title="Stats" />

      {/* Mobile only: desktop reaches Legal from the sidebar's bottom-left
          group (AppLayout.tsx). Below md there's no sidebar, and the app
          shell drops its footer there too, so this row is the way in. */}
      <Row to="/legal" title="Legal" className="md:hidden" />

      {/* Desktop has a persistent Logout in the sidebar's bottom-left
          corner (AppLayout.tsx) - this is the mobile-only fallback, since
          that sidebar is hidden below the md breakpoint. */}
      <Button variant="destructive" full onClick={logout} className="mt-2 md:hidden">
        <LogOut size={16} /> Logout
      </Button>

      {/* Desktop shows the copyright + Terms/Privacy/contact strip in the
          app shell (AppLayout.tsx). On mobile there's no room for it above
          the bottom tab bar, and Terms/Privacy/contact are all reachable
          from the "Legal" row above, so it's simply omitted there. */}

      <Sheet open={showViewer} onClose={() => setShowViewer(false)} title={profile.name}>
        <div className="flex flex-col items-center gap-4 py-6">
          <AvatarView symbol={profile.avatarSymbol} colorHex="#3B82F6" imageKey={profile.avatarImageKey} size={220} />
          <span className="text-2xl font-bold">{profile.name}</span>
        </div>
      </Sheet>
    </div>
  );
}
